// Сессия и экраны для аналитики (tech/09 §3.1, §3.9): session_start после первого экрана, session_end при уходе в фон
// дольше 60 с или закрытии, screen_view и system_screen_view по навигатору, perf_sample раз в минуту на экране.
// При уходе в фон очередь сразу уходит через sendBeacon (E18-T02).
import type { App } from '../app/app.js';
import { storage } from '../state/storage.js';
import { flushBeacon, track } from './analytics.js';

export const BACKGROUND_END_MS = 60_000;
const LAST_END_KEY = 'lastSessionEnd';

/** Ключ экрана для screen_view: верхний лист, иначе экран стека или корень вкладки. */
export function screenKey(app: App): string {
  const s = app.nav.state;
  return s.service.at(-1)?.id ?? s.sheets.at(-1)?.id ?? app.nav.top();
}

export function startAnalyticsSession(app: App): () => void {
  let started = 0;
  let screens = 0;
  let battles = 0;
  let last = '';
  let lastSystem = '';
  let hiddenAt = 0;
  let ended = false;
  let endTimer: ReturnType<typeof setTimeout> | null = null;

  const begin = () => {
    if (started) return;
    started = Date.now();
    ended = false;
    screens = 0;
    battles = 0;
    const prev = Number(storage.get(LAST_END_KEY) ?? 0);
    track('session_start', { since_last_session_s: prev ? Math.max(0, Math.round((started - prev) / 1000)) : 0 });
  };
  const end = (reason: 'background' | 'close' | 'timeout') => {
    if (!started || ended) return;
    ended = true;
    track('session_end', { duration_s: Math.round((Date.now() - started) / 1000), battles, screens, reason });
    storage.set(LAST_END_KEY, String(Date.now()));
    started = 0;
    flushBeacon();
  };

  const onNav = () => {
    const s = app.nav.state;
    if (s.system) {
      if (s.system.id !== lastSystem) { lastSystem = s.system.id; track('system_screen_view', { screen: s.system.id, min_build: '' }); }
      return;
    }
    lastSystem = '';
    begin(); // первый экран после загрузки (load_step first_screen) — начало сессии
    const key = screenKey(app);
    if (key === last) return;
    if (key === 'SCR-FIGHT') battles++;
    screens++;
    track('screen_view', { screen: key, from: last });
    last = key;
  };
  const unsub = app.nav.subscribe(onNav);

  // Уход в фон: очередь — сразу через beacon; дольше 60 с — конец сессии (iOS и Android сворачивают без pagehide).
  const onVisibility = () => {
    if (document.hidden) {
      hiddenAt = Date.now();
      flushBeacon();
      endTimer = setTimeout(() => end('background'), BACKGROUND_END_MS);
    } else {
      if (endTimer) { clearTimeout(endTimer); endTimer = null; }
      if (ended || (hiddenAt && Date.now() - hiddenAt >= BACKGROUND_END_MS)) { end('background'); begin(); last = ''; onNav(); }
      hiddenAt = 0;
    }
  };
  const onClose = () => end('close');
  document.addEventListener('visibilitychange', onVisibility);
  addEventListener('pagehide', onClose);
  app.wa?.onEvent?.('deactivated', onVisibility);

  // perf_sample: средний и нижний 10% FPS по кадрам за минуту на экране (tech/09 §3.9).
  let frames: number[] = [];
  let prevT = 0;
  let raf = 0;
  const loop = (t: number) => { if (prevT) frames.push(t - prevT); prevT = t; raf = requestAnimationFrame(loop); };
  raf = requestAnimationFrame(loop);
  const perf = setInterval(() => {
    if (document.hidden || frames.length < 30) { frames = []; return; }
    const fps = frames.map((d) => 1000 / d).sort((a, b) => a - b);
    const avg = fps.reduce((x, y) => x + y, 0) / fps.length;
    const heap = (performance as unknown as { memory?: { usedJSHeapSize: number } }).memory?.usedJSHeapSize;
    track('perf_sample', { screen: last, fps_avg: Math.round(avg), fps_p10: Math.round(fps[Math.floor(fps.length * 0.1)]!), long_tasks: 0, js_heap_mb: heap ? Math.round(heap / 1048576) : 0, draw_calls: 0, device_tier: '' });
    frames = [];
  }, 60_000);

  return () => {
    unsub();
    document.removeEventListener('visibilitychange', onVisibility);
    removeEventListener('pagehide', onClose);
    if (endTimer) clearTimeout(endTimer);
    cancelAnimationFrame(raf);
    clearInterval(perf);
  };
}
